import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import PropTypes from "prop-types";

import { formatDecimalToTime, parseTimeInput } from "@/utils/timeFormatUtils";

import styles from "./LabourTimeInput.module.scss";

const LabourTimeInput = forwardRef((props, ref) => {
  const {
    className,
    inputClassName,
    value,
    onChange,
    onValueChange,
    onBlur,
    placeholder = "0h 0m",
    disabled,
    min = 0,
    max,
    showFormatHint = true,
    ...rest
  } = props;

  const [displayValue, setDisplayValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const internalRef = useRef(null);
  const inputRef = ref || internalRef;

  // Convert decimal hours to display format
  const getDisplayValue = useCallback((decimalValue) => {
    if (typeof decimalValue === "number" && !Number.isNaN(decimalValue)) {
      return formatDecimalToTime(decimalValue);
    }
    return "0h 0m";
  }, []);

  // Initialize display value from prop
  useEffect(() => {
    if (!isEditing) {
      const decimal = parseFloat(value) || 0;
      setDisplayValue(getDisplayValue(decimal));
    }
  }, [value, isEditing, getDisplayValue]);

  const validateDecimalValue = useCallback(
    (decimalValue) => {
      let validatedValue = decimalValue;

      if (min !== undefined && validatedValue < min) {
        validatedValue = min;
      }

      if (max !== undefined && validatedValue > max) {
        validatedValue = max;
      }

      return validatedValue;
    },
    [min, max]
  );

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setShowHint(showFormatHint);

    // Clear the display value on focus if it's 0, so the placeholder becomes visible
    const decimal = parseFloat(value) || 0;
    if (decimal === 0) {
      setDisplayValue("");
    } else {
      setDisplayValue(getDisplayValue(decimal));
      // Select all text for easy replacement
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 0);
    }
  }, [value, showFormatHint, inputRef, getDisplayValue]);

  const handleChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      setDisplayValue(inputValue);

      // Call onChange if provided
      if (onChange) {
        onChange(e);
      }
    },
    [onChange]
  );

  const handleBlur = useCallback(
    (e) => {
      setIsEditing(false);
      setShowHint(false);

      const inputValue = e.target.value.trim();
      let decimalValue = 0;

      if (inputValue) {
        // Try to parse the input in various formats
        decimalValue = parseTimeInput(inputValue);

        // If parsing failed, try as a plain decimal number
        if (decimalValue === 0 && !Number.isNaN(parseFloat(inputValue))) {
          decimalValue = parseFloat(inputValue);
        }
      }

      // Validate the decimal value
      const validatedValue = validateDecimalValue(decimalValue);

      // Update display to formatted time
      setDisplayValue(getDisplayValue(validatedValue));

      // Call onBlur if provided
      if (onBlur) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: validatedValue.toString(),
          },
        };
        onBlur(syntheticEvent);
      }

      // Call onValueChange with validated decimal value
      if (onValueChange) {
        onValueChange({
          formattedValue: getDisplayValue(validatedValue),
          value: validatedValue.toString(),
          floatValue: validatedValue,
        });
      }
    },
    [validateDecimalValue, getDisplayValue, onBlur, onValueChange]
  );

  const handleKeyDown = useCallback((e) => {
    // Handle Enter key to blur and format
    if (e.key === "Enter") {
      e.target.blur();
    }
  }, []);

  return (
    <div className={cx(styles.container, className)}>
      <input
        {...rest}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={cx(styles.input, inputClassName)}
      />
      {showHint && (
        <div className={styles.formatHint}>
          Enter time in hours or minutes (e.g., 1h 30m, 1:30, 1.5).
        </div>
      )}
    </div>
  );
});

LabourTimeInput.displayName = "LabourTimeInput";

LabourTimeInput.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  showFormatHint: PropTypes.bool,
};

export default LabourTimeInput;
